import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Download, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/utils/formatCurrency';
import { adsApi, api } from '@/api/client';
import {
  entityTypeLabel,
  formatSnapshotStatus,
  parseBidSnapshot,
  parseBidSnapshotApplyJob,
  parseBidSnapshotApplyResult,
  parseBidSnapshotItem,
  parseBidSnapshotList,
  snapshotEntityTotal,
  type BidSnapshot,
  type BidSnapshotApplyJob,
  type BidSnapshotApplyResult,
  type BidSnapshotItem,
} from '@/api/bidSnapshotTypes';
import { useBidSnapshotRestore } from '@/hooks/useBidSnapshotRestore';
import { useBidSnapshotSave } from '@/hooks/useBidSnapshotSave';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { Spinner } from '@/components/ui/Spinner';

function defaultSnapshotName(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Snapshot ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function statusBadgeClass(status: string): string {
  if (status === 'ready' || status === 'completed') return 'bg-success-subtle text-success-ink border-success/20';
  if (status === 'partial' || status === 'completed_with_errors') return 'bg-warning-subtle text-warning-ink border-warning/20';
  if (status === 'failed') return 'bg-error-subtle text-error-ink border-error/20';
  if (status === 'capturing' || status === 'running' || status === 'pending') {
    return 'bg-brand-subtle text-brand border-brand/20';
  }
  return 'bg-page text-foreground-muted border-border';
}

type BidSnapshotsSectionProps = {
  profileId: string;
};

export function BidSnapshotsSection({ profileId }: BidSnapshotsSectionProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { save, isSaving } = useBidSnapshotSave(profileId);
  const { restore, isRestoring } = useBidSnapshotRestore(profileId);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState(defaultSnapshotName);
  const [saveNote, setSaveNote] = useState('');
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'saved' | 'apply'>('saved');
  const [restoreSnapshotId, setRestoreSnapshotId] = useState('');
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [activeApplyJobId, setActiveApplyJobId] = useState<string | null>(null);
  const [itemSearch, setItemSearch] = useState('');
  const [resultStatusFilter, setResultStatusFilter] = useState('');

  const listQuery = useQuery({
    queryKey: ['bid-snapshots', profileId],
    queryFn: async () => {
      const res = await adsApi.listBidSnapshots(profileId, { limit: 20 });
      return parseBidSnapshotList(res.data);
    },
    enabled: !!profileId,
    refetchInterval: (query) => {
      const list = query.state.data ?? [];
      const capturing = list.some((s) => s.status === 'capturing');
      return capturing || isSaving || isRestoring ? 2000 : false;
    },
  });

  const activeSnapshotQuery = useQuery({
    queryKey: ['bid-snapshot', profileId, activeSnapshotId],
    queryFn: async () => {
      const res = await adsApi.getBidSnapshot(profileId, activeSnapshotId!);
      return parseBidSnapshot(res.data);
    },
    enabled: !!profileId && !!activeSnapshotId,
  });

  const itemsQuery = useQuery({
    queryKey: ['bid-snapshot-items', profileId, activeSnapshotId, itemSearch],
    queryFn: async () => {
      const res = await adsApi.listBidSnapshotItems(profileId, activeSnapshotId!, {
        search: itemSearch || undefined,
        limit: 100,
      });
      const data = res.data as { items?: unknown[]; total?: number };
      const items = (data.items ?? []).map(parseBidSnapshotItem).filter((x): x is BidSnapshotItem => x != null);
      return { items, total: Number(data.total ?? items.length) };
    },
    enabled: !!profileId && !!activeSnapshotId && detailTab === 'saved',
  });

  const applyJobsQuery = useQuery({
    queryKey: ['bid-snapshot-apply-jobs', profileId, activeSnapshotId],
    queryFn: async () => {
      const res = await adsApi.listBidSnapshotApplyJobs(profileId, activeSnapshotId!);
      const data = res.data as { jobs?: unknown[] };
      return (data.jobs ?? [])
        .map(parseBidSnapshotApplyJob)
        .filter((x): x is BidSnapshotApplyJob => x != null);
    },
    enabled: !!profileId && !!activeSnapshotId && detailTab === 'apply',
  });

  const applyJobQuery = useQuery({
    queryKey: ['bid-snapshot-apply-job', profileId, activeApplyJobId],
    queryFn: async () => {
      const res = await adsApi.getBidSnapshotApplyJob(profileId, activeApplyJobId!);
      return parseBidSnapshotApplyJob(res.data);
    },
    enabled: !!profileId && !!activeApplyJobId && detailTab === 'apply',
  });

  const applyResultsQuery = useQuery({
    queryKey: ['bid-snapshot-apply-results', profileId, activeApplyJobId, resultStatusFilter],
    queryFn: async () => {
      const res = await adsApi.listBidSnapshotApplyResults(profileId, activeApplyJobId!, {
        status: resultStatusFilter || undefined,
        limit: 100,
      });
      const data = res.data as { results?: unknown[]; total?: number };
      const results = (data.results ?? [])
        .map(parseBidSnapshotApplyResult)
        .filter((x): x is BidSnapshotApplyResult => x != null);
      return { results, total: Number(data.total ?? results.length) };
    },
    enabled: !!profileId && !!activeApplyJobId && detailTab === 'apply',
    refetchInterval: applyJobQuery.data?.status === 'running' ? 2000 : false,
  });

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshots', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshot', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['sp-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sb-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sp-targets'] });
  }, [profileId, queryClient]);

  const handleSave = async () => {
    try {
      const snap = await save({
        name: saveName.trim() || defaultSnapshotName(),
        note: saveNote.trim() || undefined,
      });
      if (snap.status === 'failed') {
        showToast(snap.capture_error || 'Snapshot capture failed', 'error');
      } else if (snap.status === 'partial') {
        showToast('Snapshot saved with partial data', 'success');
      } else {
        showToast(`Snapshot saved (${snapshotEntityTotal(snap.entity_counts)} entities)`, 'success');
      }
      setSaveOpen(false);
      setSaveNote('');
      setActiveSnapshotId(snap.id);
      setDetailTab('saved');
      invalidateAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save snapshot', 'error');
    }
  };

  const handleRestore = async () => {
    if (!restoreSnapshotId) return;
    try {
      const job = await restore(restoreSnapshotId);
      const failed = job.totals.failed ?? 0;
      if (job.status === 'failed') {
        showToast('Restore failed', 'error');
      } else if (failed > 0) {
        showToast(`Restore finished with ${failed} failure(s)`, 'success');
      } else {
        showToast(`Restore complete (${job.totals.applied ?? 0} bids updated)`, 'success');
      }
      setRestoreOpen(false);
      setActiveSnapshotId(job.snapshot_id);
      setActiveApplyJobId(job.id);
      setDetailTab('apply');
      invalidateAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to restore bids', 'error');
    }
  };

  const retryCaptureMutation = useMutation({
    mutationFn: (snapshotId: string) => adsApi.retryBidSnapshotCapture(profileId, snapshotId),
    onSuccess: () => {
      showToast('Retrying capture…', 'success');
      invalidateAll();
    },
    onError: (err: Error) => showToast(err.message || 'Retry failed', 'error'),
  });

  const retryApplyMutation = useMutation({
    mutationFn: (jobId: string) => adsApi.retryBidSnapshotApply(profileId, jobId),
    onSuccess: () => {
      showToast('Retrying failed applies…', 'success');
      invalidateAll();
    },
    onError: (err: Error) => showToast(err.message || 'Retry failed', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (snapshotId: string) => adsApi.deleteBidSnapshot(profileId, snapshotId),
    onSuccess: () => {
      showToast('Snapshot deleted', 'success');
      setActiveSnapshotId(null);
      invalidateAll();
    },
    onError: (err: Error) => showToast(err.message || 'Delete failed', 'error'),
  });

  const downloadApplyCsv = useCallback(async (jobId: string) => {
    try {
      const url = adsApi.exportBidSnapshotApplyResultsUrl(profileId, jobId);
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bid-apply-${jobId}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      showToast('CSV export failed', 'error');
    }
  }, [profileId, showToast]);

  const snapshots = listQuery.data ?? [];
  const latest = snapshots[0] ?? null;
  const restorable = useMemo(
    () => snapshots.filter((s) => s.status === 'ready' || s.status === 'partial'),
    [snapshots]
  );

  useEffect(() => {
    if (!restoreSnapshotId && restorable.length > 0) {
      setRestoreSnapshotId(restorable[0]!.id);
    }
  }, [restorable, restoreSnapshotId]);

  const activeSnap = activeSnapshotQuery.data;
  const activeJob = applyJobQuery.data;

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">Bid snapshots</h3>
          <p className="mt-1 text-sm text-foreground-muted">
            Save all keyword and product-target bids before a sale, then restore them when the sale ends.
          </p>
          {latest && (
            <p className="mt-2 text-xs text-foreground-subtle">
              Latest: <strong>{latest.name}</strong> · {snapshotEntityTotal(latest.entity_counts)} entities ·{' '}
              {latest.created_at ? new Date(latest.created_at).toLocaleString() : '—'}
              {latest.catalog_as_of && (
                <>
                  {' '}
                  · Catalog as of {new Date(latest.catalog_as_of).toLocaleString()}
                </>
              )}
            </p>
          )}
          {latest?.catalog_stale && (
            <p className="mt-2 rounded border border-warning/20 bg-warning-subtle px-2 py-1 text-xs text-warning-ink">
              Entity catalog may be out of date. Snapshots use the last synced catalog, not a live Amazon pull.
              {latest.catalog_as_of
                ? ` Last sync: ${new Date(latest.catalog_as_of).toLocaleString()}.`
                : ' No sync timestamp found.'}
            </p>
          )}
          {(isSaving || isRestoring) && (
            <p className="mt-2 flex items-center gap-2 text-xs text-brand">
              <Spinner className="h-3.5 w-3.5" />
              {isSaving ? 'Saving snapshot…' : 'Restoring bids…'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => {
              setSaveName(defaultSnapshotName());
              setSaveOpen(true);
            }}
            disabled={isSaving}
            size="md"
          >
            <Camera className="h-4 w-4" />
            Save snapshot
          </Button>
          <Button
            disabled={restorable.length === 0 || isRestoring}
            onClick={() => setRestoreOpen(true)}
            variant="secondary"
            size="md"
          >
            <RotateCcw className="h-4 w-4" />
            Restore
          </Button>
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-foreground-subtle">
          <Spinner className="h-4 w-4" />
          Loading snapshots…
        </div>
      ) : snapshots.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-foreground-subtle">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Entities</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((snap: BidSnapshot) => (
                <tr key={snap.id} className="border-b border-chrome-divider">
                  <td className="py-2 pr-4 font-medium text-foreground">{snap.name}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex rounded border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(snap.status)}`}
                    >
                      {formatSnapshotStatus(snap.status)}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-foreground-muted">{snapshotEntityTotal(snap.entity_counts)}</td>
                  <td className="py-2 pr-4 text-foreground-muted">
                    {snap.created_at ? new Date(snap.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="text-brand-deep hover:text-brand hover:underline"
                        onClick={() => {
                          setActiveSnapshotId(snap.id);
                          setDetailTab('saved');
                        }}
                      >
                        View saved
                      </button>
                      {(snap.status === 'partial' || snap.status === 'failed') && (
                        <button
                          type="button"
                          className="text-warning-ink hover:underline"
                          onClick={() => retryCaptureMutation.mutate(snap.id)}
                        >
                          Retry capture
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-error hover:underline"
                        onClick={() => {
                          if (window.confirm(`Delete snapshot "${snap.name}"?`)) {
                            deleteMutation.mutate(snap.id);
                          }
                        }}
                      >
                        <Trash2 className="inline h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-sm text-foreground-subtle">No snapshots yet. Save one before your next sale.</p>
      )}

      {activeSnapshotId && activeSnap && (
        <div className="mt-6 rounded-xl border border-border bg-page p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-medium text-foreground">{activeSnap.name}</h4>
            <button type="button" className="text-sm text-foreground-subtle hover:text-foreground" onClick={() => setActiveSnapshotId(null)}>
              Close
            </button>
          </div>
          {activeSnap.status === 'failed' && activeSnap.capture_error && (
            <p className="mt-2 text-sm text-error-ink">{activeSnap.capture_error}</p>
          )}
          {activeSnap.catalog_stale && (
            <p className="mt-2 text-xs text-warning-ink">
              Captured from catalog
              {activeSnap.catalog_as_of
                ? ` synced ${new Date(activeSnap.catalog_as_of).toLocaleString()}`
                : ''}
              {' '}
              (may be stale).
            </p>
          )}
          {activeSnap.status === 'partial' && (
            <p className="mt-2 text-sm text-warning-ink">
              Partial capture: {snapshotEntityTotal(activeSnap.entity_counts)} saved.
              {Number(activeSnap.entity_counts.total_failed_capture ?? 0) > 0
                ? ` ${activeSnap.entity_counts.total_failed_capture} fetch issue(s).`
                : ''}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={`rounded px-3 py-1 text-sm ${detailTab === 'saved' ? 'bg-surface shadow' : 'text-foreground-muted'}`}
              onClick={() => setDetailTab('saved')}
            >
              Saved items
            </button>
            <button
              type="button"
              className={`rounded px-3 py-1 text-sm ${detailTab === 'apply' ? 'bg-surface shadow' : 'text-foreground-muted'}`}
              onClick={() => setDetailTab('apply')}
            >
              Apply history
            </button>
          </div>

          {detailTab === 'saved' && (
            <div className="mt-3">
              <input
                type="search"
                placeholder="Search keyword / target…"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="mb-2 w-full max-w-sm rounded border border-border px-2 py-1.5 text-sm"
              />
              {itemsQuery.isLoading ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <ItemsTable items={itemsQuery.data?.items ?? []} total={itemsQuery.data?.total ?? 0} />
              )}
            </div>
          )}

          {detailTab === 'apply' && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                {(applyJobsQuery.data ?? []).map((job) => (
                  <button
                    key={job.id}
                    type="button"
                    onClick={() => setActiveApplyJobId(job.id)}
                    className={`rounded border px-2 py-1 text-xs ${
                      activeApplyJobId === job.id ? 'border-selection-border bg-selection-bar text-brand-ink' : 'border-border bg-surface'
                    }`}
                  >
                    {job.created_at ? new Date(job.created_at).toLocaleString() : job.id.slice(0, 8)} ·{' '}
                    {formatSnapshotStatus(job.status)}
                  </button>
                ))}
              </div>
              {activeJob && (
                <div className="text-sm text-foreground-muted">
                  Applied: {activeJob.totals.applied ?? 0} · Failed: {activeJob.totals.failed ?? 0} · Skipped:{' '}
                  {activeJob.totals.skipped ?? 0}
                  {(activeJob.status === 'completed_with_errors' || (activeJob.totals.failed ?? 0) > 0) && (
                    <button
                      type="button"
                      className="ml-3 inline-flex items-center gap-1 text-warning-ink hover:underline"
                      onClick={() => retryApplyMutation.mutate(activeJob.id)}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Retry failed
                    </button>
                  )}
                  <button
                    type="button"
                    className="ml-3 inline-flex items-center gap-1 text-brand-deep hover:text-brand hover:underline"
                    onClick={() => downloadApplyCsv(activeJob.id)}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export CSV
                  </button>
                </div>
              )}
              {activeApplyJobId && (
                <>
                  <select
                    value={resultStatusFilter}
                    onChange={(e) => setResultStatusFilter(e.target.value)}
                    className="rounded border border-border px-2 py-1 text-sm"
                  >
                    <option value="">All statuses</option>
                    <option value="applied">Applied</option>
                    <option value="failed">Failed</option>
                    <option value="skipped">Skipped</option>
                    <option value="pending">Pending</option>
                  </select>
                  <ApplyResultsTable results={applyResultsQuery.data?.results ?? []} total={applyResultsQuery.data?.total ?? 0} />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {saveOpen && (
        <Modal title="Save bid snapshot" onClose={() => !isSaving && setSaveOpen(false)}>
          <label className="block text-sm font-medium text-foreground-muted">Name</label>
          <input
            className="mt-1 mb-3 w-full rounded border border-border px-2 py-1.5 text-sm"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            disabled={isSaving}
          />
          <label className="block text-sm font-medium text-foreground-muted">Note (optional)</label>
          <textarea
            className="mt-1 mb-4 w-full rounded border border-border px-2 py-1.5 text-sm"
            rows={2}
            value={saveNote}
            onChange={(e) => setSaveNote(e.target.value)}
            disabled={isSaving}
          />
          <p className="mb-4 text-xs text-foreground-subtle">
            Saves all SP/SB keyword and product-target bids from your synced entity catalog (no live Amazon pull).
          </p>
          {isSaving && (
            <p className="mb-3 flex items-center gap-2 text-sm text-brand">
              <Spinner className="h-4 w-4" />
              Capturing bids…
            </p>
          )}
          <ModalActions
            confirmLabel={isSaving ? 'Saving…' : 'Save snapshot'}
            onConfirm={() => void handleSave()}
            onCancel={() => setSaveOpen(false)}
            disabled={isSaving}
          />
        </Modal>
      )}

      {restoreOpen && (
        <Modal title="Restore bids from snapshot" onClose={() => !isRestoring && setRestoreOpen(false)}>
          <p className="mb-3 text-sm text-foreground-muted">
            This will overwrite current bids on Amazon for all entities in the selected snapshot.
          </p>
          <select
            className="mb-4 w-full rounded border border-border px-2 py-1.5 text-sm"
            value={restoreSnapshotId}
            onChange={(e) => setRestoreSnapshotId(e.target.value)}
            disabled={isRestoring}
          >
            {restorable.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({snapshotEntityTotal(s.entity_counts)} entities)
              </option>
            ))}
          </select>
          {isRestoring && (
            <p className="mb-3 flex items-center gap-2 text-sm text-brand">
              <Spinner className="h-4 w-4" />
              Applying bids on Amazon…
            </p>
          )}
          <ModalActions
            confirmLabel={isRestoring ? 'Restoring…' : 'Restore bids'}
            onConfirm={() => void handleRestore()}
            onCancel={() => setRestoreOpen(false)}
            disabled={isRestoring || !restoreSnapshotId}
            confirmVariant="warning"
          />
        </Modal>
      )}
    </div>
  );
}

function ItemsTable({ items, total }: { items: BidSnapshotItem[]; total: number }) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-surface">
      <table className="min-w-full text-xs">
        <thead className="bg-page text-left text-foreground-subtle">
          <tr>
            <th className="px-2 py-1.5">Type</th>
            <th className="px-2 py-1.5">Label / ID</th>
            <th className="px-2 py-1.5">Bid</th>
            <th className="px-2 py-1.5">State</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-t border-chrome-divider">
              <td className="px-2 py-1.5">{entityTypeLabel(item.entity_type)}</td>
              <td className="px-2 py-1.5">
                <div>{item.entity_label || item.entity_id}</div>
                {item.entity_label && <div className="text-chrome-label">{item.entity_id}</div>}
              </td>
              <td className="px-2 py-1.5">{formatCurrency(item.bid, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td className="px-2 py-1.5">{item.state ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-2 py-1 text-xs text-foreground-subtle">Showing {items.length} of {total}</p>
    </div>
  );
}

function ApplyResultsTable({ results, total }: { results: BidSnapshotApplyResult[]; total: number }) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-surface">
      <table className="min-w-full text-xs">
        <thead className="bg-page text-left text-foreground-subtle">
          <tr>
            <th className="px-2 py-1.5">Type</th>
            <th className="px-2 py-1.5">Entity</th>
            <th className="px-2 py-1.5">Snapshot bid</th>
            <th className="px-2 py-1.5">Status</th>
            <th className="px-2 py-1.5">Error</th>
          </tr>
        </thead>
        <tbody>
          {results.map((row) => (
            <tr key={row.id} className="border-t border-chrome-divider">
              <td className="px-2 py-1.5">{entityTypeLabel(row.entity_type)}</td>
              <td className="px-2 py-1.5">{row.entity_label || row.entity_id}</td>
              <td className="px-2 py-1.5">{formatCurrency(row.snapshot_bid, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td className="px-2 py-1.5">
                <span className={`rounded border px-1.5 py-0.5 ${statusBadgeClass(row.status)}`}>
                  {row.status}
                </span>
              </td>
              <td className="max-w-xs truncate px-2 py-1.5 text-error-ink" title={row.error_message ?? undefined}>
                {row.error_message ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-2 py-1 text-xs text-foreground-subtle">Showing {results.length} of {total}</p>
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-base font-semibold text-foreground">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  confirmLabel,
  onConfirm,
  onCancel,
  disabled,
  confirmVariant = 'primary',
}: {
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  confirmVariant?: 'primary' | 'warning';
}) {
  return (
    <div className="flex justify-end gap-2">
      <Button
        onClick={onCancel}
        disabled={disabled}
        variant="secondary"
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={disabled}
        variant={confirmVariant}
      >
        {confirmLabel}
      </Button>
    </div>
  );
}
